import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { getTheme } from '@styles/theme'

const STORAGE_KEY = 'themeMode'
const ThemeModeContext = createContext(null)

// App boots in light; users opt into the dark neon look and the choice sticks.
const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'dark' || stored === 'light' ? stored : 'light'
  } catch {
    return 'light'
  }
}

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider')
  }
  return context
}

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(readStoredMode)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // Ignore storage failures (private mode, quota) — mode still applies.
    }
  }, [mode])

  const setThemeMode = useCallback((next) => {
    setMode(next === 'dark' ? 'dark' : 'light')
  }, [])

  const toggleMode = useCallback(() => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  const theme = useMemo(() => getTheme(mode), [mode])

  const value = useMemo(
    () => ({ mode, isDark: mode === 'dark', toggleMode, setThemeMode }),
    [mode, toggleMode, setThemeMode],
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export default ThemeModeContext
